import { Tag } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type { TFunction } from "i18next";
import type React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierPaymentMethod,
  CashierPaymentMethodGroup,
  CurrencyPrimitive,
  EnumDisabledReason,
  LastUsedPaymentMethod,
  SavedPaymentAccountIdAndFields,
} from "@/api/cashier";
import { DeleteSavedPaymentDialog } from "@/components/delete-saved-payment-dialog/DeleteSavedPaymentDialog";
import { DepositBankNotice } from "@/components/deposit-bank-notice/DepositBankNotice";
import { PaymentFormFields } from "@/components/forms/PaymentFormFields";
import { Icon } from "@/components/general/icon/Icon";
import { SavedPaymentMethods } from "@/components/method-list/saved-payment-methods/SavedPaymentMethods";
import { SelectableCard } from "@/components/selectable-card/SelectableCard";
import { SelectableCardSkeleton } from "@/components/selectable-card/SelectableCardSkeleton";
import { Tooltip } from "@/components/tooltip/Tooltip";
import {
  getUnambiguousSavedAccount,
  usePreFillLastUsedPaymentMethod,
} from "@/hooks/deposit/usePreFillLastUsedPaymentMethod";
import { EIcon } from "@/utils/enums";
import {
  isBankTransfer,
  isCardForm,
  isPayPal,
  moneyToMajorUnits,
} from "@/utils/helpers";
import {
  getDisabledReasonTooltip,
  isPaymentMethodOffline,
} from "@/utils/paymentMethodStatus";
import { useIsMobile } from "@/hooks/useIsMobile";
import styles from "./MethodList.module.css";
import { usePaymentMethodProperties } from "./usePaymentMethodProperties";

type MethodListProps = {
  tradingCurrency: CurrencyPrimitive;
  licenceId: string;
  paymentMethods?: (CashierPaymentMethod | CashierPaymentMethodGroup)[];
  lastUsedPaymentMethod?: LastUsedPaymentMethod;
  selectedPaymentMethodChange?: (
    paymentMethod: CashierPaymentMethod,
    paymentMethodGroup?: CashierPaymentMethodGroup,
    savedPaymentAccount?: SavedPaymentAccountIdAndFields,
    isUserInitiated?: boolean
  ) => void;
  isLoading?: boolean;
  disabled?: boolean;
  onPaymentMethodsUpdated?: () => void;
  onAddingNewAccountChange?: (isAddingNew: boolean) => void;
};

const checkIsDisabled = (method: CashierPaymentMethod) =>
  isPaymentMethodOffline(method);

const withTooltipIfOffline = (
  key: React.Key,
  paymentMethod: CashierPaymentMethod | CashierPaymentMethodGroup,
  t: TFunction,
  children: React.ReactNode
) => {
  let showTooltip: boolean;
  let disabledReason: EnumDisabledReason | undefined;

  if ("payment_methods" in paymentMethod) {
    showTooltip = paymentMethod.payment_methods.every(checkIsDisabled);
    disabledReason = paymentMethod.payment_methods.find(
      (m) => m.disabled_reason
    )?.disabled_reason;
  } else {
    showTooltip = checkIsDisabled(paymentMethod);
    disabledReason = paymentMethod.disabled_reason;
  }

  const tooltipContent = getDisabledReasonTooltip(t, disabledReason);

  return showTooltip && tooltipContent ? (
    <Tooltip
      key={key}
      content={tooltipContent}
      className={styles["payment-method-offline-tooltip"]}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};

export const MethodList = ({
  tradingCurrency,
  licenceId,
  paymentMethods,
  lastUsedPaymentMethod,
  selectedPaymentMethodChange,
  isLoading,
  disabled = false,
  onPaymentMethodsUpdated,
  onAddingNewAccountChange,
}: MethodListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { getPaymentMethodProperties } = usePaymentMethodProperties();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<
    CashierPaymentMethod | undefined
  >();
  const [selectedMethodGroup, setSelectedMethodGroup] = useState<
    CashierPaymentMethodGroup | undefined
  >();
  const [selectedSavedPaymentAccount, setSelectedSavedPaymentAccount] =
    useState<SavedPaymentAccountIdAndFields | undefined>();
  const [showSavedAccountsControl, setShowSavedAccountsControl] =
    useState(true);

  const handleSelectAccount = useCallback(
    (
      paymentMethod: CashierPaymentMethod,
      paymentMethodGroup?: CashierPaymentMethodGroup,
      savedPaymentAccount?: SavedPaymentAccountIdAndFields,
      isUserInitiated = true
    ) => {
      if (
        paymentMethod === selectedMethod &&
        paymentMethodGroup === selectedMethodGroup &&
        savedPaymentAccount === selectedSavedPaymentAccount
      ) {
        // do nothing when we click on the same method on desktop, on mobile we want to open the MobileBottomSheet component
        if (isMobile) {
          selectedPaymentMethodChange?.(
            paymentMethod,
            paymentMethodGroup,
            savedPaymentAccount,
            isUserInitiated
          );
        }
        return;
      }
      onAddingNewAccountChange?.(false);
      if (paymentMethod.id !== selectedMethod?.id || !savedPaymentAccount) {
        setShowSavedAccountsControl(true);
      }
      setSelectedMethod(paymentMethod);
      setSelectedMethodGroup(paymentMethodGroup);
      setSelectedSavedPaymentAccount(savedPaymentAccount);
      selectedPaymentMethodChange?.(
        paymentMethod,
        paymentMethodGroup,
        savedPaymentAccount,
        isUserInitiated
      );
    },
    [
      selectedPaymentMethodChange,
      selectedMethod,
      selectedMethodGroup,
      selectedSavedPaymentAccount,
      onAddingNewAccountChange,
      isMobile,
    ]
  );

  const showSavedAccounts = (paymentMethod: CashierPaymentMethod) => {
    if (!paymentMethod.saved_payment_accounts?.length) {
      return false;
    }
    return showSavedAccountsControl;
  };

  const hasChildren = (paymentMethod: CashierPaymentMethod) => {
    return showSavedAccounts(paymentMethod) || isBankTransfer(paymentMethod);
  };

  const getSelectedSavedPaymentAccount = (
    paymentMethod: CashierPaymentMethod
  ) => {
    return paymentMethod?.saved_payment_accounts?.length === 1
      ? paymentMethod?.saved_payment_accounts?.[0]
      : selectedSavedPaymentAccount;
  };

  const {
    orderedPaymentMethods,
    preSelectedPaymentId,
    hasLasUsedPaymentMethod,
  } = usePreFillLastUsedPaymentMethod({
    paymentMethods,
    lastUsedPaymentMethod,
    selectAccount: useCallback(
      (paymentMethod, paymentMethodGroup, savedPaymentAccount) =>
        handleSelectAccount(
          paymentMethod,
          paymentMethodGroup,
          savedPaymentAccount,
          false
        ),
      [handleSelectAccount]
    ),
  });

  const getGroupTags = (paymentMethodGroup: CashierPaymentMethodGroup) => {
    const availableItems = paymentMethodGroup.payment_methods.filter(
      (paymentMethod) => !checkIsDisabled(paymentMethod)
    ).length;

    const tags: React.ReactNode[] = [
      <Tag
        key={"tag-availability"}
        bgColor="foreground-neutral"
        size="md"
        label={t("deposit.methods.group_items_available", {
          count: availableItems,
        })}
      />,
    ];

    if (
      preSelectedPaymentId === paymentMethodGroup.id &&
      hasLasUsedPaymentMethod
    ) {
      tags.push(
        <Tag
          key={"tag-last-used"}
          bgColor="foreground-neutral"
          size="md"
          label={t("deposit.methods.last_used")}
        />
      );
    }

    return tags;
  };

  const renderOrderedPaymentMethods = () =>
    orderedPaymentMethods?.map((method) => {
      const isGroup = "payment_methods" in method;
      if (!isGroup) {
        const paymentMethod = method as CashierPaymentMethod;
        const depositSettings =
          "deposit" in paymentMethod.base_settings
            ? paymentMethod.base_settings.deposit
            : undefined;

        return withTooltipIfOffline(
          paymentMethod.id,
          paymentMethod,
          t,
          <SelectableCard
            key={method.id}
            className={clsx(
              styles.card,
              paymentMethod.id === selectedMethod?.id && styles.cardSelected
            )}
            id={paymentMethod.id}
            icon={paymentMethod.logo_url}
            iconPadding="xxs"
            title={paymentMethod.display_name}
            subText={getPaymentMethodProperties({
              paymentMethod,
              tradingCurrency,
            })}
            extraContent={depositSettings?.info_message}
            tag={
              preSelectedPaymentId === paymentMethod.id &&
              hasLasUsedPaymentMethod && (
                <Tag
                  key={"tag-last-used"}
                  bgColor="foreground-neutral"
                  size="md"
                  label={t("deposit.methods.last_used")}
                />
              )
            }
            onClick={() =>
              handleSelectAccount(
                paymentMethod,
                undefined,
                getUnambiguousSavedAccount(paymentMethod)
              )
            }
            onChildrenClick={(event) => event.stopPropagation()}
            selected={paymentMethod.id === selectedMethod?.id}
            expanded={paymentMethod.id === selectedMethod?.id}
            hideChildSeparatorLine={!hasChildren(paymentMethod)}
            disabled={checkIsDisabled(method) || disabled}
          >
            {paymentMethod.id === selectedMethod?.id &&
              (isBankTransfer(paymentMethod) ? (
                <DepositBankNotice
                  fee={
                    paymentMethod.settings?.[tradingCurrency.code]?.fee?.value
                      ? (moneyToMajorUnits(
                          paymentMethod.settings[tradingCurrency.code].fee.value
                        ) ?? 0)
                      : 0
                  }
                  currency={tradingCurrency}
                />
              ) : showSavedAccounts(paymentMethod) ? (
                <SavedPaymentMethods
                  selected={getSelectedSavedPaymentAccount(method)}
                  paymentMethod={paymentMethod}
                  onSelect={(savedPaymentAccount) => {
                    handleSelectAccount(
                      paymentMethod,
                      undefined,
                      savedPaymentAccount
                    );
                  }}
                  onAddNewClick={() => {
                    setShowSavedAccountsControl(false);
                    setSelectedSavedPaymentAccount(undefined);
                    onAddingNewAccountChange?.(true);
                    selectedPaymentMethodChange?.(
                      paymentMethod,
                      undefined,
                      undefined
                    );
                  }}
                  onDeleteClick={() => {
                    setShowDeleteModal(true);
                  }}
                  savedPaymentAccounts={paymentMethod.saved_payment_accounts}
                  onExpiryUpdated={onPaymentMethodsUpdated}
                  showAddNewButton={!isPayPal(paymentMethod)}
                />
              ) : (
                <div className={styles["new-card-container"]}>
                  <PaymentFormFields
                    className={styles["inline-form-in-container"]}
                    formClassName="ps-flex ps-flex-col ps-gap-3"
                    licenceId={licenceId}
                    paymentMethod={paymentMethod}
                  />
                  {paymentMethod.saved_payment_accounts?.length ? (
                    <div className={styles["back-to-saved-wrapper"]}>
                      <button
                        type="button"
                        className={styles["back-to-saved"]}
                        onClick={() => {
                          setShowSavedAccountsControl(true);
                          onAddingNewAccountChange?.(false);
                          const unambiguous =
                            getUnambiguousSavedAccount(paymentMethod);
                          if (unambiguous) {
                            handleSelectAccount(
                              paymentMethod,
                              undefined,
                              unambiguous
                            );
                          }
                        }}
                      >
                        <Icon
                          name={EIcon.ArrowLeftNarrow}
                          className={styles["back-to-saved-icon"]}
                          alt=""
                          aria-hidden="true"
                        />
                        {isCardForm(paymentMethod)
                          ? t("deposit.methods.saved_methods.back_to_saved")
                          : t(
                              "deposit.methods.saved_methods.back_to_saved_method"
                            )}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
          </SelectableCard>
        );
      }
      const paymentMethodGroup = method as CashierPaymentMethodGroup;
      return withTooltipIfOffline(
        paymentMethodGroup.id,
        paymentMethodGroup,
        t,
        <SelectableCard
          className={clsx(
            styles.card,
            paymentMethodGroup.id === selectedMethodGroup?.id &&
              styles.cardSelected
          )}
          key={method.id}
          id={paymentMethodGroup.id}
          icon={paymentMethodGroup.logo_url}
          title={paymentMethodGroup.display_name}
          selected={paymentMethodGroup.id === selectedMethodGroup?.id}
          tag={getGroupTags(paymentMethodGroup)}
          expandable
          expandedByDefault={paymentMethodGroup.id === selectedMethodGroup?.id}
          onClick={() =>
            handleSelectAccount(
              paymentMethodGroup.payment_methods[0],
              paymentMethodGroup
            )
          }
          onChildrenClick={(event) => event.stopPropagation()}
          disabled={
            paymentMethodGroup.payment_methods.every(checkIsDisabled) ||
            disabled
          }
        >
          <div className={styles["group-container"]}>
            {paymentMethodGroup.payment_methods.map((paymentMethod) => {
              const depositSettings =
                "deposit" in paymentMethod.base_settings
                  ? paymentMethod.base_settings.deposit
                  : undefined;
              return (
                <div
                  key={paymentMethod.id}
                  className={clsx(
                    styles["group-container-item"],
                    paymentMethod.id === selectedMethod?.id && styles.selected
                  )}
                >
                  {withTooltipIfOffline(
                    paymentMethod.id,
                    paymentMethod,
                    t,
                    <SelectableCard
                      key={paymentMethod.id}
                      id={paymentMethod.id}
                      // icon={paymentMethod.logo_url}
                      // iconPadding="xxs"
                      title={paymentMethod.display_name}
                      subText={getPaymentMethodProperties({
                        paymentMethod,
                        tradingCurrency,
                      })}
                      extraContent={depositSettings?.info_message}
                      onClick={() =>
                        handleSelectAccount(paymentMethod, selectedMethodGroup)
                      }
                      onChildrenClick={(event) => event.stopPropagation()}
                      selected={paymentMethod.id === selectedMethod?.id}
                      hideChildSeparatorLine
                      disabled={checkIsDisabled(paymentMethod) || disabled}
                    >
                      {paymentMethod.id === selectedMethod?.id &&
                        (showSavedAccounts(paymentMethod) ? (
                          <SavedPaymentMethods
                            paymentMethod={paymentMethod}
                            savedPaymentAccounts={
                              paymentMethod.saved_payment_accounts
                            }
                            selected={getSelectedSavedPaymentAccount(
                              paymentMethod
                            )}
                            onSelect={(savedPaymentAccount) => {
                              handleSelectAccount(
                                paymentMethod,
                                paymentMethodGroup,
                                savedPaymentAccount
                              );
                            }}
                            onAddNewClick={() => {
                              setShowSavedAccountsControl(false);
                              setSelectedSavedPaymentAccount(undefined);
                              onAddingNewAccountChange?.(true);
                              selectedPaymentMethodChange?.(
                                paymentMethod,
                                paymentMethodGroup,
                                undefined
                              );
                            }}
                            showAddNewButton={!isPayPal(paymentMethod)}
                          />
                        ) : (
                          <div className={styles["new-card-container"]}>
                            <PaymentFormFields
                              className={styles["inline-form-in-container"]}
                              formClassName="ps-flex ps-flex-col ps-gap-3"
                              licenceId={licenceId}
                              paymentMethod={paymentMethod}
                            />
                            {paymentMethod.saved_payment_accounts?.length ? (
                              <div className={styles["back-to-saved-wrapper"]}>
                                <button
                                  type="button"
                                  className={styles["back-to-saved"]}
                                  onClick={() => {
                                    setShowSavedAccountsControl(true);
                                    onAddingNewAccountChange?.(false);
                                    const unambiguous =
                                      getUnambiguousSavedAccount(paymentMethod);
                                    if (unambiguous) {
                                      handleSelectAccount(
                                        paymentMethod,
                                        paymentMethodGroup,
                                        unambiguous
                                      );
                                    }
                                  }}
                                >
                                  <Icon
                                    name={EIcon.ArrowLeftNarrow}
                                    className={styles["back-to-saved-icon"]}
                                    alt=""
                                    aria-hidden="true"
                                  />
                                  {isCardForm(paymentMethod)
                                    ? t(
                                        "deposit.methods.saved_methods.back_to_saved"
                                      )
                                    : t(
                                        "deposit.methods.saved_methods.back_to_saved_method"
                                      )}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ))}
                    </SelectableCard>
                  )}
                </div>
              );
            })}
          </div>
        </SelectableCard>
      );
    });

  if (isLoading)
    return (
      <div className={styles.container} data-testid="account-list">
        <SelectableCardSkeleton />
        <SelectableCardSkeleton />
        <SelectableCardSkeleton />
        <SelectableCardSkeleton />
      </div>
    );

  return (
    <>
      <div className={styles.container} data-testid="account-list">
        {renderOrderedPaymentMethods()}
      </div>
      {selectedSavedPaymentAccount && (
        <DeleteSavedPaymentDialog
          onConfirm={() => {
            setShowDeleteModal(false);
            onPaymentMethodsUpdated?.();
          }}
          isOpen={showDeleteModal}
          onCancel={() => {
            setShowDeleteModal(false);
          }}
          selectedSavedPaymentAccount={selectedSavedPaymentAccount}
        />
      )}
    </>
  );
};
